import React, { Suspense, lazy, useState, useEffect } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { SpotlightPageProps, FootballPageProps, V3TemplatePageProps } from 'src/components/spotlight/module-util';
import { useStores } from 'src/utils/useStore';
import PageLoader from 'src/components/common/modules/loader/page-loader';
import { checkValue } from 'src/components/functions';

const FootballHub = lazy(() => import('src/components/football/pages'));
const Spotlight = lazy(() => import('src/components/spotlight/page'));
const V3Template = lazy(() => import('src/components/v3-template/page'));
const FootballTest = lazy(() => import('src/components/football/pages/football-test'));
const MediaStudio = lazy(() => import('src/components/media-studio/pages'));
const ConciergePage = lazy(() => import('src/components/concierge/pages'));
const TncPage = lazy(() => import('src/components/tnc/page'));
const TtnqOneOffPage = lazy(() => import('src/components/ttnqOneoff/page'));
const LeeCountyOneoffPage = lazy(() => import('src/components/leeCountyOneoffPage/page'));
const PensacolaOneoffPage = lazy(() => import('src/components/pensacolaOneoff/page'));
const CpdsDemo = lazy(() => import('src/components/common/cpds/demo'));

interface RouterProps {
  match?: {
    [key: string]: any;
    params?: {
      [key: string]: any;
      feedName?: string;
    };
  };
  location?: any;
}

const getTemplateParams = (store) => {
  if (checkValue(store) && checkValue(store?.templateParams)) {
    const { partner, templateName, feedName, feedName2 } = store?.templateParams;
    return { partner, templateName, feedName, feedName2 };
  }
}

export const FootballHubView: React.FC<RouteComponentProps> = (props) => {
  const { cpcmslitefeed, sanityStore, context } = useStores();
  const { location: query }: RouterProps = props;
  const [isDomLoaded, setIsDomLoaded] = useState(false);

  useEffect(() => {
    const handleDomContentLoaded = () => {
      setIsDomLoaded(true);
    };

    if (document.readyState === 'complete' || document.readyState === 'interactive') {
      // Document is already ready
      handleDomContentLoaded();
    } else {
      // Add event listener for DOMContentLoaded
      window.addEventListener('DOMContentLoaded', handleDomContentLoaded);

      // Cleanup event listener on component unmount
      return () => {
        window.removeEventListener('DOMContentLoaded', handleDomContentLoaded);
      };
    }
  }, []);

  // Check Page Data Source is Umbraco Or Sanity
  const activeStore = () => {
    return checkValue(cpcmslitefeed) && checkValue(cpcmslitefeed.campaignName)
      ? cpcmslitefeed
      : checkValue(sanityStore) && checkValue(sanityStore.campaignName)
        ? sanityStore
        : undefined;
  };

  const activeCampaign = (store) => {
    try {
      return checkValue(store) && checkValue(store?.campaignName) ? store?.campaignName : '';
    } catch (error) {
      console.log('Error', error);
      return '';
    }
  };

  const store = activeStore();
  // Get template parameters
  const { partner, templateName, feedName, feedName2 } = getTemplateParams(store);
 
  const renderView = () => {
    // This is test code need to remove checkDataAvailable once test done
    //const checkDataAvailable = cpcmslitefeed?.cpCmsLitePageData?.error;
    //const campaignName = checkDataAvailable === undefined ? cpcmslitefeed?.campaignName : '';

    const campaignName = activeCampaign(store);

    switch (campaignName) {
      case 'spotlight': {
        const templateProps: SpotlightPageProps = {
          partner: partner || '',
          templateName: templateName || '',
          feedName: feedName || '',
        };
      
        return (
          <>
            <Spotlight {...templateProps} />
          </>
        );
      }
      case 'football': {
        // this is for test pr
        const footballPageProps: FootballPageProps = {
          partner: partner || '',
          templateName: templateName || '',
          feedName: feedName || '',
        };
      
        return (
          <>
            <FootballHub {...footballPageProps} />
          </>
        );
      }
      case 'v3template': {
        // this is for test pr
        const v3TemplatePageProps: V3TemplatePageProps = {
          partner: partner || '',
          templateName: templateName || '',
          feedName: feedName || '',
          query: query || {},
        };       

        return (
          <>
            <V3Template {...v3TemplatePageProps} />
          </>
        );
      }
      case 'media-studio': {
        const mediaStudioProps: any = {
          page: partner || '',
          partner: partner || '',
          templateName: templateName || '',
          feedName: feedName || '',
          feedName2: feedName2 || '',
        };
        return (
          <>
            <MediaStudio {...mediaStudioProps} />
          </>
        );
      }
      case 'concierge': {
        const ConciergePageProps = {
          partner: partner || '',
          context: context || '',
          templateName: templateName || '',
          feedName: feedName || '',
        };
       
        return <ConciergePage {...ConciergePageProps} />;
      }
      case 'tnc': {
        return (
          <>
            <TncPage />
          </>
        );
      }
      case 'ttnqOneoff': {
        return (
          <>
            <TtnqOneOffPage />
          </>
        );
      }
      case 'leeCountyOneoff': {
        return (
          <>
            <LeeCountyOneoffPage />
          </>
        );
      }
      case 'pensacolaOneoff': {
        return (
          <>
            <PensacolaOneoffPage />
          </>
        );
      }
      case 'test': {
        return (
          <>
            <FootballTest />
          </>
        );
      }
      case 'cpds-demo': {
        return (
          <>
            <CpdsDemo />
          </>
        );
      }
      default: {
        const pageRfrr = !checkValue(templateName) ? 'cplite.sports' : templateName;
        window.location.href = `/404/?rfrr=cplite.${pageRfrr}`;
      }
    }
  };

  const renderLoader = () => <PageLoader />;

  if (!isDomLoaded || typeof window !== 'undefined' && (!checkValue(store) || !checkValue(store.campaignName))) {
    return <>{renderLoader()}</>;
  }

  return (
    checkValue(store) &&
    checkValue(store?.campaignName) &&
    typeof window !== 'undefined' && isDomLoaded && <Suspense fallback={renderLoader()}>{renderView()}</Suspense>
  );
};

FootballHubView.displayName = 'FootballHub';

export default FootballHubView;
